import React from 'react';
import { T, FONT_STACK } from './customers.theme';

/**
 * Icons and controls the customers book shares.
 *
 * Stroke icons on a 24px grid, drawn inline so they inherit colour and pull no
 * font or network request into a screen people leave open all day.
 */

const base = (size) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

export const PlusIcon = ({ size = 14, width = 2.4 }) => (
  <svg {...base(size)} strokeWidth={width}><path d="M12 5v14M5 12h14" /></svg>
);

export const SearchIcon = ({ size = 16, width = 1.9 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4.5 4.5" />
  </svg>
);

export const CloseIcon = ({ size = 17, width = 2.2 }) => (
  <svg {...base(size)} strokeWidth={width}><path d="M6 6l12 12M18 6L6 18" /></svg>
);

export const PeopleIcon = ({ size = 24, width = 1.8 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <circle cx="12" cy="8.5" r="3.8" />
    <path d="M4.8 20c0-3.4 3.2-5.6 7.2-5.6s7.2 2.2 7.2 5.6" />
  </svg>
);

export const ListIcon = ({ size = 26, width = 1.7 }) => (
  <svg {...base(size)} strokeWidth={width}><path d="M4 5.5h16M4 12h16M4 18.5h10" /></svg>
);

export const CalendarIcon = ({ size = 16, width = 1.8 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
    <path d="M3.5 10h17M8 3.5v3M16 3.5v3" />
  </svg>
);

export const PhoneIcon = ({ size = 15, width = 1.9 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M4.5 5.5c0 8 6 14 14 14l2-3-4-2.5-2 2a14 14 0 0 1-6.5-6.5l2-2L7.5 3.5z" />
  </svg>
);

export const StatementIcon = ({ size = 15, width = 1.9 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M6 3.5h8.5L19 8v12.5H6z" /><path d="M14 3.5V8h5" />
    <path d="M9 12.5h7M9 16h5" />
  </svg>
);

export const EditIcon = ({ size = 15, width = 1.9 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M14.5 4.5l5 5L9 20H4v-5z" /><path d="M12.5 6.5l5 5" />
  </svg>
);

export const TrashIcon = ({ size = 15, width = 1.9 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M4 6.5h16M9.5 6.5V4.5h5v2M6.5 6.5V20h11V6.5" />
  </svg>
);

export const AlertIcon = ({ size = 15, width = 2 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.5M12 16.4v.01" />
  </svg>
);

export const ChevronIcon = ({ size = 16, width = 2.2, up }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d={up ? 'M18 14.5l-6-6-6 6' : 'M6 9.5l6 6 6-6'} />
  </svg>
);

export const Spinner = ({ size = 16, width = 2.6 }) => (
  <svg
    {...base(size)}
    strokeWidth={width}
    style={{ animation: 'dalabtireCustomersSpin 0.8s linear infinite' }}
  >
    <path d="M12 3.5a8.5 8.5 0 1 1-8.5 8.5" opacity="0.4" />
    <path d="M12 3.5a8.5 8.5 0 0 1 8.5 8.5" />
  </svg>
);

/** The one keyframe this screen needs, owned by it rather than App.css. */
export const CustomersKeyframes = () => (
  <style>
    {'@keyframes dalabtireCustomersSpin { to { transform: rotate(360deg); } }'}
  </style>
);

/* ── controls ───────────────────────────────────────────────────────────── */

/**
 * A pill field, matching the mobile app's rounded inputs rather than the
 * square ones on the orders screen — this book is the phone's screen brought
 * across, and its shapes came with it.
 */
export function PillField({
  label,
  name,
  value,
  onChange,
  onEnter,
  placeholder,
  type = 'text',
  autoFocus,
  disabled,
  invalid,
  trailing,
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label ? (
        <label htmlFor={name} style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
          {label}
        </label>
      ) : null}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          height: 46,
          padding: '0 16px',
          background: disabled ? T.sunk : T.surface,
          border: `${invalid ? 1.5 : 1}px solid ${invalid ? T.brand : T.borderStrong}`,
          borderRadius: T.radiusPill,
          boxShadow: invalid ? `0 0 0 3px ${T.brandTint}` : 'none',
          boxSizing: 'border-box',
        }}
      >
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && onEnter) {
              event.preventDefault();
              onEnter();
            }
          }}
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            font: 'inherit',
            fontFamily: FONT_STACK,
            fontSize: 14,
            color: T.ink,
          }}
        />
        {trailing || null}
      </div>
    </div>
  );
}

const buttonBase = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  border: 'none',
  borderRadius: T.radiusControl,
  font: 'inherit',
  fontFamily: FONT_STACK,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export function PrimaryButton({ children, onClick, disabled, busy, type = 'button', style }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || busy}
      style={{
        ...buttonBase,
        height: 48,
        background: T.brand,
        color: T.surface,
        fontSize: 15,
        fontWeight: 500,
        borderRadius: 10,
        opacity: disabled ? 0.45 : busy ? 0.72 : 1,
        cursor: disabled || busy ? 'not-allowed' : 'pointer',
        ...style,
      }}
    >
      {busy ? <Spinner /> : null}
      <span>{children}</span>
    </button>
  );
}

export function GhostButton({ children, onClick, disabled, style }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...buttonBase,
        height: 48,
        background: T.surface,
        border: `1px solid ${T.borderStrong}`,
        color: T.inkSoft,
        fontSize: 14,
        fontWeight: 600,
        borderRadius: 10,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

/** A centred card over a dim. Escape closes it, never mid-request. */
export function Dialog({ children, onClose, width = 420, locked }) {
  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !locked) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, locked]);

  return (
    <div
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !locked) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: T.backdrop,
        fontFamily: FONT_STACK,
      }}
    >
      <div
        style={{
          width,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          background: T.surface,
          borderRadius: T.radiusDialog,
          boxShadow: T.dialog,
          boxSizing: 'border-box',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function DialogHeader({ title, onClose, locked }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        paddingBottom: 14,
        borderBottom: `1px solid ${T.border}`,
      }}
    >
      <span style={{ fontSize: 19, fontWeight: 500, color: T.ink }}>{title}</span>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        disabled={locked}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 30,
          border: 'none',
          borderRadius: T.radiusControl,
          background: 'transparent',
          color: locked ? T.inkGhost : T.inkFaint,
          cursor: locked ? 'not-allowed' : 'pointer',
        }}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

export function ErrorLine({ children }) {
  if (!children) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: T.danger }}>
      <AlertIcon size={14} />
      <span style={{ fontSize: 12.5, fontWeight: 600 }}>{children}</span>
    </div>
  );
}
